import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getHotComics, getLatestUpdates } from '@/mocks/comics';

export default function Home() {
  const hotComics = getHotComics();
  const latestComics = getLatestUpdates();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫之家 - 免费韩国漫画大全',
    description: '韩漫之家让您免费在线阅读好看的韩国漫画大全，界面友好加载快速，随时随地享受阅读乐趣。',
    url: 'https://hanmanzhijia.com',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'https://hanmanzhijia.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: hotComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicStory',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://hanmanzhijia.com/article/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <PageMeta
        title="韩漫之家 - 免费韩国漫画大全 - 好看的漫画在线阅读"
        description="韩漫之家让您免费在线阅读好看的韩国漫画大全，涵盖恋爱、奇幻、悬疑、动作、搞笑等十大漫画品类。界面友好加载快速，漫画资源持续更新，随时随地享受阅读乐趣。"
        keywords="韩漫之家,免费韩国漫画,好看的漫画,在线阅读,韩国漫画,韩漫,漫画大全"
        canonicalUrl="https://hanmanzhijia.com"
        schema={schema}
      />
      <ScrollToTopButton />

      <main>
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20abstract%20illustration%20with%20soft%20gradients%20of%20warm%20peach%20and%20cream%20tones%2C%20stylized%20manga%20panel%20borders%20subtly%20integrated%20into%20the%20background%2C%20gentle%20geometric%20shapes%20floating%2C%20elegant%20and%20inviting%20atmosphere%2C%20clean%20modern%20design%20with%20Asian%20aesthetic%20influences%2C%20soft%20ethereal%20lighting%2C%20contemporary%20art%20style&width=1920&height=900&seq=home-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading leading-tight">
              发现好看的<strong className="text-primary-300">韩国漫画</strong>
            </h1>
            <p className="mt-4 md:mt-6 text-sm md:text-lg text-background-200 max-w-2xl mx-auto leading-relaxed">
              韩漫之家汇集海量免费韩国漫画资源，恋爱、奇幻、悬疑、动作、搞笑——十大品类随心畅读。
              加载快速，界面友好，随时随地享受阅读乐趣。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/romance"
                className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 border border-background-300 hover:border-background-100 text-background-100 hover:text-background-50 font-medium rounded-full text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {[
                { value: '500+', label: '漫画资源' },
                { value: '200万+', label: '月活用户' },
                { value: '4.8', label: '用户评分' },
              ].map(stat => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-xs text-background-300 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 py-12 md:py-16" aria-labelledby="category-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 id="category-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                漫画分类
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                十大品类，随心选择你喜欢的漫画类型
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
                >
                  <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap font-heading">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="bg-background-100 py-12 md:py-16" aria-labelledby="hot-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 id="hot-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  热门推荐
                </h2>
                <p className="mt-1 text-sm text-foreground-500">最受欢迎的韩国漫画</p>
              </div>
              <Link
                to="/category/romance"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap transition-colors cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
              {hotComics.map(comic => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  rating={comic.rating}
                  views={comic.views}
                  tags={comic.tags}
                  status={comic.status}
                  categoryName={comic.categoryName}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="bg-background-50 py-12 md:py-16" aria-labelledby="latest-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 id="latest-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  最新更新
                </h2>
                <p className="mt-1 text-sm text-foreground-500">刚刚更新的漫画章节</p>
              </div>
              <Link
                to="/category/romance"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap transition-colors cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
              {latestComics.map(comic => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  rating={comic.rating}
                  views={comic.views}
                  tags={comic.tags}
                  status={comic.status}
                  categoryName={comic.categoryName}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-background-100 py-12 md:py-16" aria-labelledby="feature-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 id="feature-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                为什么选择韩漫之家
              </h2>
              <p className="mt-2 text-sm text-foreground-500">数百万读者的共同选择</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '500+韩国漫画，十大品类全覆盖' },
                { icon: 'ri-smartphone-line', title: '极致体验', desc: '界面清爽流畅，加载如丝般顺滑' },
                { icon: 'ri-translate', title: '专业汉化', desc: '资深团队翻译，原汁原味呈现' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '同步韩国连载，第一时间更新' },
              ].map(feat => (
                <div
                  key={feat.title}
                  className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${feat.icon} text-xl w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20abstract%20background%20with%20soft%20peach%20and%20cream%20gradients%2C%20subtle%20manga%20speech%20bubble%20shapes%20floating%2C%20gentle%20warm%20lighting%2C%20inviting%20cozy%20atmosphere%2C%20contemporary%20minimalist%20design%2C%20soft%20ethereal%20texture%2C%20Asian%20aesthetic%20elements&width=1920&height=600&seq=home-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading">
              准备好开始阅读了吗？
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed">
              现在加入韩漫之家，海量免费韩国漫画等你来发现
            </p>
            <Link
              to="/category/romance"
              className="mt-8 inline-block px-10 py-4 bg-accent-500 hover:bg-accent-600 text-background-50 font-bold rounded-full text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              免费阅读热门漫画
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}